import { FC } from "react";
import {
    Button,
    DataTable,
    DataTableActions,
    DataTableColumn,
    DataTableFilter,
    Switch,
    Tag,
    Tooltip,
} from "../../design-system";
import { User, UserRole, UserStatus } from "../../types/UserGroup";
import { formatDateTime, getRelativeTime } from "../date/DateOp";
import styles from "./MembersTable.module.less";

/** Wristband invitation links expire after 7 days. */
const INVITE_EXPIRY_MS = 7 * 24 * 60 * 60 * 1000;

const invited = (member: User) =>
    member.status === UserStatus.CREATED || member.status === UserStatus.WAITING;

const inviteExpired = (member: User) =>
    member.status === UserStatus.WAITING &&
    !!member.invitedAt &&
    Date.now() - new Date(member.invitedAt).getTime() > INVITE_EXPIRY_MS;

const isAdmin = (member: User) => member.roles.includes(UserRole.ADMIN);

const STATUS_TAGS: Record<string, { label: string; color: string }> = {
    // The row exists but the invitation never went out (the first send failed).
    [UserStatus.CREATED]: { label: "Invite not sent", color: "gold" },
    [UserStatus.WAITING]: { label: "Invited", color: "blue" },
    [UserStatus.ACTIVE]: { label: "Active", color: "green" },
    [UserStatus.INACTIVE]: { label: "Inactive", color: "red" },
};

const statusLabel = (member: User) =>
    inviteExpired(member) ? "Invite expired" : (STATUS_TAGS[member.status]?.label ?? member.status);

const statusTag = (member: User) => {
    if (inviteExpired(member)) return <Tag color="orange">Invite expired</Tag>;
    const tag = STATUS_TAGS[member.status];
    return tag ? <Tag color={tag.color}>{tag.label}</Tag> : <Tag>{member.status}</Tag>;
};

/** The status, and while an invitation is pending, how long ago it was last sent. */
const status = (member: User) => (
    <>
        {statusTag(member)}
        {invited(member) && member.invitedAt && (
            <Tooltip title={`Invitation sent ${formatDateTime(member.invitedAt)}`}>
                <span className={styles.invitedAt}>{getRelativeTime(member.invitedAt)}</span>
            </Tooltip>
        )}
    </>
);

const lastLogin = (member: User) =>
    member.lastLoginAt ? (
        <Tooltip title={formatDateTime(member.lastLoginAt)}>
            <span>{getRelativeTime(member.lastLoginAt)}</span>
        </Tooltip>
    ) : null;

export const MEMBER_FILTERS: DataTableFilter<User>[] = [
    { key: "all", label: "All" },
    {
        key: "active",
        label: "Active",
        match: (member) => member.status === UserStatus.ACTIVE,
        emptyText: "No active members.",
    },
    {
        key: "invited",
        label: "Invited",
        match: invited,
        emptyText: "No pending invitations.",
    },
    {
        key: "inactive",
        label: "Inactive",
        match: (member) => member.status === UserStatus.INACTIVE,
        emptyText: "No deactivated members.",
    },
];

export interface MembersTableProps {
    members: User[];
    /** The signed-in admin: their own row keeps its admin role and cannot be deactivated. */
    selfId: number;
    /** The member whose change is in flight, so the row shows it. */
    pendingId?: number;
    onSetAdmin: (member: User, admin: boolean) => void;
    /** Sends (or resends) the invitation of a member who has not accepted yet. */
    onInvite: (member: User) => void;
    onDeactivate: (member: User) => void;
    onReactivate: (member: User) => void;
}

/** The organization's members, for its admins to manage. */
export const MembersTable: FC<MembersTableProps> = ({
    members,
    selfId,
    pendingId,
    onSetAdmin,
    onInvite,
    onDeactivate,
    onReactivate,
}) => {
    const columns: DataTableColumn<User>[] = [
        {
            key: "name",
            title: "Name",
            sort: "text",
            sortValue: (member) => member.name,
            ellipsis: true,
            render: (member) => (
                <>
                    {member.name}
                    {member.id === selfId && <span className={styles.you}>(you)</span>}
                </>
            ),
        },
        {
            key: "email",
            title: "Email",
            sort: "text",
            sortValue: (member) => member.email,
            ellipsis: true,
            render: (member) => member.email,
        },
        {
            key: "admin",
            title: "Admin",
            sort: "number",
            sortValue: (member) => (isAdmin(member) ? 1 : 0),
            filter: {
                type: "enum",
                options: [
                    { value: "admin", label: "Admin" },
                    { value: "member", label: "Member" },
                ],
                value: (member) => (isAdmin(member) ? "admin" : "member"),
            },
            render: (member) => (
                <Switch
                    size="small"
                    checked={isAdmin(member)}
                    // An admin cannot drop their own role: the server refuses
                    // it, so the org cannot lock itself out.
                    disabled={member.id === selfId}
                    loading={pendingId === member.id}
                    onChange={(checked: boolean) => onSetAdmin(member, checked)}
                />
            ),
        },
        {
            key: "status",
            title: "Status",
            sort: "text",
            sortValue: statusLabel,
            filter: {
                type: "enum",
                options: Object.entries(STATUS_TAGS).map(([value, { label }]) => ({
                    value,
                    label,
                })),
                value: (member) => member.status,
            },
            render: status,
        },
        {
            key: "lastLogin",
            title: "Last login",
            sort: "date",
            sortValue: (member) => member.lastLoginAt,
            render: lastLogin,
        },
        {
            key: "actions",
            title: "Actions",
            width: "140px",
            actions: true,
            // One plain button per row: what the member's status calls for.
            // An admin cannot deactivate themselves (the server refuses it,
            // so the org cannot lock itself out), so their row has none.
            render: (member) => (
                <DataTableActions>
                    {invited(member) && (
                        <Button
                            size="small"
                            loading={pendingId === member.id}
                            onClick={() => onInvite(member)}
                        >
                            {member.status === UserStatus.CREATED ? "Send invite" : "Resend invite"}
                        </Button>
                    )}
                    {member.status === UserStatus.ACTIVE && member.id !== selfId && (
                        <Button
                            size="small"
                            danger
                            loading={pendingId === member.id}
                            onClick={() => onDeactivate(member)}
                        >
                            Deactivate
                        </Button>
                    )}
                    {member.status === UserStatus.INACTIVE && (
                        <Button
                            size="small"
                            loading={pendingId === member.id}
                            onClick={() => onReactivate(member)}
                        >
                            Reactivate
                        </Button>
                    )}
                </DataTableActions>
            ),
        },
    ];

    return (
        <DataTable<User>
            columns={columns}
            rows={members}
            rowKey={(member) => member.id}
            defaultSort={{ key: "name" }}
            filters={MEMBER_FILTERS}
            searchText={(member) => [member.name, member.email]}
            searchPlaceholder="Search members"
            emptyText="No members yet. Invite your colleagues to get started."
            storageKey="members"
        />
    );
};

MembersTable.displayName = "MembersTable";
